import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '../../../../../components/@extended/Avatar';
import { Chip } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

// project imports
import IconButton from '../../../../../components/@extended/IconButton';

import { handlerDrawerOpen, useGetMenuMaster } from '../../../../../api/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

import { Theme, alpha } from '@mui/material/styles';

interface NavItemProps {
  item: any;
  level: number;
  isParents?: boolean;
  setSelectedID?: (id: string) => void;
}

export default function NavItem({ item, level, isParents = false, setSelectedID }: NavItemProps) {
  const { t } = useTranslation();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened || false;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const { pathname } = useLocation();
  const itemPath = item?.link || item.url;
  const isActionItem = typeof item.action === 'function' && !itemPath;
  const isSelected = itemPath ? !!matchPath({ path: itemPath, end: false }, pathname) : false;

  const itemHandler = (event?: React.MouseEvent) => {
    if (isActionItem) {
      event?.preventDefault();
      item.action();
    }

    // Si el drawer está cerrado y el item está seleccionado, abrir el drawer
    if (!drawerOpen && isSelected) {
      handlerDrawerOpen(true);
      return;
    }

    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  // Función para manejar clic en el icono
  const handleIconClick = (event: React.MouseEvent) => {
    if (isActionItem) return;

    event.preventDefault();
    event.stopPropagation();
    
    // Si el drawer está cerrado, abrirlo
    if (!drawerOpen) {
      handlerDrawerOpen(true);
    }
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1.35rem' : '1.35rem',
        ...(isParents && { fontSize: drawerOpen ? 24 : 20, stroke: '1.5' }),
        color: isSelected && drawerOpen ? 'white' : 'inherit'
      }}
    />
  ) : (
    false
  );

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.contrastText';
  const label = item?.titleKey ? t(item.titleKey) : item.title;
  const showTooltip = !drawerOpen && level === 1;

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Tooltip title={showTooltip ? label : ''} placement="right" arrow disableHoverListener={!showTooltip}>
          <Box component="span" sx={{ display: 'block' }}>
            <ListItemButton
              {...(isActionItem
                ? { component: 'div' as const, role: 'button', tabIndex: 0 }
                : { component: Link, to: item.url, target: itemTarget })}
              disabled={item.disabled}
              selected={isSelected}
              sx={(theme: any) => ({
                zIndex: 1201,
                width: '100%',
                pl: drawerOpen ? `${level * 14}px` : 1.5,
                py: !drawerOpen && level === 1 ? 1.25 : 1,
                transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                ...(isActionItem && {
                  cursor: 'pointer',
                  border: 0,
                  backgroundColor: 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }),
                ...(drawerOpen && {
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.light, 0.15),
                    ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                  },
                  '&.Mui-selected': {
                    position: 'relative',
                    bgcolor: 'primary.main',
                    borderRight: '2px solid',
                    borderColor: 'primary.dark',
                    color: iconSelectedColor,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 3,
                      borderRadius: 2,
                      bgcolor: 'primary.dark',
                      transition: 'background-color 150ms ease'
                    },
                    '&:hover': {
                      color: iconSelectedColor,
                      bgcolor: 'primary.dark',
                      '&::before': { bgcolor: 'primary.dark' }
                    }
                  }
                }),
                ...(!drawerOpen && {
                  '&:hover': { bgcolor: 'transparent' },
                  '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
                })
              })}
              onClick={itemHandler}
            >
          {itemIcon && (
            <ListItemIcon
              onClick={handleIconClick}
              sx={(theme: any) => ({
                minWidth: drawerOpen ? 36 : 28,
                color: isSelected ? iconSelectedColor : textColor,
                ...(drawerOpen && {
                  borderRadius: 1.5,
                  ...(!isActionItem && {
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.light, 0.15),
                      ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                    }
                  })
                }),
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.light, 0.15),
                    ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'secondary.lighter',
                    ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }),
                    '&:hover': { bgcolor: 'secondary.light' }
                  })
              })}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                  {label}
                </Typography>
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color as any}
              variant={item.chip.variant as any}
              size={item.chip.size as any}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
            </ListItemButton>
          </Box>
        </Tooltip>
        {(drawerOpen || (!drawerOpen && level !== 1)) &&
          item?.actions &&
          item?.actions.map((action: any, index: any) => {
            const ActionIcon = action.icon;
            const callAction = action?.function;
            return (
              <IconButton
                key={index}
                {...(action.type === 'function' && {
                  onClick: (event: any) => {
                    event.stopPropagation();
                    callAction();
                  }
                })}
                {...(action.type === 'link' && {
                  component: Link,
                  to: action.url,
                  target: action.target ? '_blank' : '_self'
                })}
                color="secondary"
                variant="outlined"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 20,
                  zIndex: 1202,
                  width: 20,
                  height: 20,
                  mr: -1,
                  ml: 1,
                  color: 'secondary.dark',
                  borderColor: isSelected ? 'secondary.main' : 'secondary.light',
                  '&:hover': { borderColor: isSelected ? 'secondary.dark' : 'secondary.main' }
                }}
              >
                <ActionIcon style={{ fontSize: '0.625rem' }} />
              </IconButton>
            );
          })}
      </Box>
    </>
  );
}

